import { dodgeAttackSources } from '../../../shared/rules/monkActions.mjs';
import { empoweredStrikesType } from '../../../shared/rules/monk.mjs';
import {isCriticalAttackRoll} from '../../../shared/rules/champion.mjs';
import { applyDamageTypeTraits } from '../../../shared/rules/damage/damageTypeTraits.mjs';
import { damageByType as splitDamageByType } from '../../../shared/rules/damage/damageByType.mjs';
import { guidingBoltEffects, nextAttackDisadvantageEffects } from '../../../shared/rules/effects/directSpellRiders.mjs';
import { consumeGuidingBolt, consumeSpellAttackEffects } from './directSpellRiderService';
import { buildResolvedHitContext, rollDamageComponents } from './attackFeatureContracts';
import { getActiveVexEffect, qualifiesForVex, VEX_ADVANTAGE_SOURCE } from './weaponMasteryVex';
import { SHIELD_AC_BONUS } from '../../../shared/rules/effects/reactionSpells.mjs';
import { cursedAttackSources, enfeebledAttackSource, hasBlur, hasFaerieFire, hasForesight, hasHolyAura, isDancing, seesThroughInvisible, sizeChangeMode } from '../../../shared/rules/effects/buffModifiers.mjs';
import { createSlowDecision, qualifiesForSlow } from './weaponMasterySlow';
import { getActiveSapEffects, qualifiesForSap, SAP_DISADVANTAGE_SOURCE } from './weaponMasterySap';
import { createPushDecision, getPushOptions, getPushSpatialContext, qualifiesForPush } from './weaponMasteryPush';
import { createToppleDecision, createToppleSaveRequest, getToppleSaveDC, qualifiesForTopple } from './weaponMasteryTopple';
import { attackDistanceFeet, getConditionAttackSources, getProneAttackSources, hasPackAlly } from './conditions';
import { hasBeastTrait } from '../../../shared/rules/beastForm';
import { isBloodied } from '../../../shared/rules/subclasses/cleric/life.mjs';
import { calculateCharacterHitPoints } from './characterMetrics';
import { hasActiveEffect } from './combatTimeline';
import { GRAZE_DEFINITION_ID } from './weaponMasteryGraze';
import { getInvisibleAttackSources } from '../classFeatures/rogue/cunningAction';
import { getSteadyAimAdvantage } from '../classFeatures/rogue/steadyAim';
import { applyIncomingDamageModifiers } from './reactions';
import { dispelEvilAttackSources, protectionFromEvilAttackSources, shiningSmiteAttackSources } from '../../../shared/rules/effects/buffModifiers.mjs';
import { grapplerAttackAdvantages } from '../../../shared/rules/unarmedOptions.mjs';
import { attackThroughDoor, getAttackCover, getDeflectingWall, getFrightenedAttackSources, getObscuredAttackSources, getWeaponRangeDisadvantages } from './weaponRangeAttack';
import { FORGO_ALL_ADVANTAGE, withoutSuppressedAdvantages } from './advantageSuppression';
import { hasElusive } from '../../../shared/rules/rogue.mjs';
import { charmedByEffects } from '../../../shared/rules/frightened.mjs';
import { getStudiedAttacksEffect, hasStudiedAttacks, STUDIED_ATTACKS_ADVANTAGE_SOURCE } from '../../../shared/rules/fighter.mjs';
import { incapacitationReason } from '../../../shared/rules/barbarianRage.mjs';
import { MULTIATTACK_DEFENSE_DISADVANTAGE_SOURCE, PRECISE_HUNTER_ADVANTAGE_SOURCE, feralSensesPerceive, hasRangerFeature, multiattackDefenseApplies, preciseHunterApplies } from '../../../shared/rules/ranger.mjs';

export const createResolutionId = (attackerId, targetId, attackId) => {
  const uuid = typeof crypto !== 'undefined' ? crypto.randomUUID?.() : null;
  return uuid || `${attackerId}:${targetId}:${attackId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
};

/** The question asked when only a door stands between attacker and target. */
export const createDoorDecision = (resolutionId, targetName) => ({
  id: `${resolutionId}:door`,
  featureId: 'map:door',
  resolutionId,
  title: 'Door',
  label: 'Door',
  prompt: `Is the door between you and ${targetName || 'the target'} open?`,
  choices: [{ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }],
});

export const INACTIVE_COMBAT_TARGETING = Object.freeze({ status: 'inactive' });

const validateHitFeatureEvaluations = (evaluations) => {
  if (!Array.isArray(evaluations)) throw new Error('evaluateHitFeatures must return an array.');
  evaluations.forEach((evaluation) => {
    if (!evaluation || typeof evaluation !== 'object' || typeof evaluation.featureId !== 'string' || !evaluation.featureId.trim()) {
      throw new Error('Each hit feature evaluation must identify a feature.');
    }
    if (!['ineligible', 'automatic', 'confirmation-required'].includes(evaluation.status)) {
      throw new Error(`Hit feature "${evaluation.featureId}" returned an invalid status.`);
    }
    if (evaluation.status === 'ineligible') {
      if (typeof evaluation.reasonCode !== 'string' || !evaluation.reasonCode.trim()) {
        throw new Error(`Ineligible hit feature "${evaluation.featureId}" must provide a reason code.`);
      }
      return;
    }
    if (!Array.isArray(evaluation.components) || !Array.isArray(evaluation.claims)) {
      throw new Error(`Hit feature "${evaluation.featureId}" must provide component and claim arrays.`);
    }
    evaluation.claims.forEach((claim) => {
      if (!claim || claim.featureId !== evaluation.featureId || !claim.campaignId || !claim.combatantId ||
          !Number.isInteger(claim.turnSequence) || claim.turnSequence < 0 || !claim.resolutionId) {
        throw new Error(`Hit feature "${evaluation.featureId}" provided an invalid usage claim.`);
      }
    });
    if (evaluation.status === 'confirmation-required' && (!evaluation.decision || typeof evaluation.decision !== 'object')) {
      throw new Error(`Hit feature "${evaluation.featureId}" must provide a decision.`);
    }
    if (evaluation.decision != null) {
      const { decision } = evaluation;
      if (!decision || typeof decision !== 'object') {
        throw new Error(`Hit feature "${evaluation.featureId}" provided an invalid decision.`);
      }
      if (typeof decision.id !== 'string' || !decision.id.trim() || decision.featureId !== evaluation.featureId ||
          typeof decision.resolutionId !== 'string' || !decision.resolutionId.trim() ||
          typeof decision.label !== 'string' || !decision.label.trim() ||
          typeof decision.prompt !== 'string' || !decision.prompt.trim() ||
          !Array.isArray(decision.choices) || decision.choices.length === 0 ||
          decision.choices.some((choice) => !choice || typeof choice.id !== 'string' || !choice.id.trim() || typeof choice.label !== 'string' || !choice.label.trim())) {
        throw new Error(`Hit feature "${evaluation.featureId}" provided an invalid decision.`);
      }
    }
  });
  return evaluations;
};

export const getAttackRollMode = ({ attackerId, targetId, attack, attacker, target, combatState, spatialContext, proneProximity, advantageSources = [], disadvantageSources = [], suppressedAdvantageSources: requestedSuppressions = [], targetCanSeeAttacker = true } = {}) => {
  // Elusive (Rogue 18): no attack roll against the Rogue has Advantage while it isn't Incapacitated. Suppressing every
  // source here also reaches the Advantage the attacker's own roller adds (Reckless Attack).
  const elusive = target?.entityType !== 'enemy' && hasElusive(target, combatState, targetId);
  const suppressedAdvantageSources = elusive ? [...requestedSuppressions, FORGO_ALL_ADVANTAGE] : requestedSuppressions;
  const prone = getProneAttackSources({ combatState, attackerId, targetId, attacker, target, spatialContext, proximity: proneProximity });
  const invisible = getInvisibleAttackSources(combatState, attackerId, targetId, { castingSpell: ['spell','cantrip'].includes(attack?.kind) || Boolean(attack?.spellId || attack?.spell?.spellId) });
  const conditions = getConditionAttackSources({ combatState, attackerId, targetId });
  // Fog and thick foliage drawn on the map block sight both ways (2024 Heavily Obscured).
  const obscured = getObscuredAttackSources({ attackerId, targetId, spatialContext, combatState });
  // Feral Senses (Ranger 18): Blindsight perceives what Invisible and heavy obscurement hide, within 30 feet.
  const senseRange = hasRangerFeature(attacker, 'feralSenses') || hasRangerFeature(target, 'feralSenses') ? attackDistanceFeet({ attackerId, targetId, attacker, target, spatialContext, proximity: proneProximity }) : null;
  const attackerPerceives = feralSensesPerceive(attacker, senseRange), targetPerceives = feralSensesPerceive(target, senseRange);
  const advantages = [...advantageSources, ...prone.advantageSources, ...(targetPerceives || seesThroughInvisible(combatState, targetId, attackerId) ? [] : invisible.advantageSources), ...conditions.advantageSources, ...(targetPerceives ? [] : obscured.advantageSources), ...shiningSmiteAttackSources(combatState, targetId), ...grapplerAttackAdvantages(combatState, attacker, attackerId, targetId)];
  const disadvantages = [...disadvantageSources, ...prone.disadvantageSources, ...(attackerPerceives || seesThroughInvisible(combatState, attackerId, targetId) ? [] : invisible.disadvantageSources), ...conditions.disadvantageSources, ...(attackerPerceives ? [] : obscured.disadvantageSources), ...dodgeAttackSources({target,combatState,targetId,attackerId,targetCanSeeAttacker}),
    ...protectionFromEvilAttackSources(combatState, targetId, attacker),
    ...dispelEvilAttackSources(combatState, targetId, attacker),
    ...getWeaponRangeDisadvantages({ attackerId, attack, attacker, combatState, spatialContext }),
    ...getFrightenedAttackSources({ attackerId, attacker, combatState, spatialContext })];
  if (hasForesight(combatState, attackerId)) advantages.push('Foresight');
  // Traits that give Advantage, from a Druid's Beast form or an enemy's own stat block.
  if (hasBeastTrait(attacker, 'Pack Tactics') && hasPackAlly({ attackerId, targetId, attacker, target, combatState, spatialContext })) advantages.push('Pack Tactics');
  // A character's current HP is `tempHealth`; an enemy's is `currentHp` against its `hitPoints`.
  const bloodied = attacker?.tempHealth == null ? isBloodied(Number(attacker?.currentHp), Number(attacker?.maxHp ?? attacker?.hitPoints))
    : isBloodied(Number(attacker.tempHealth), Number(calculateCharacterHitPoints(attacker)?.maxHp));
  if (hasBeastTrait(attacker, 'Bloodied Fury') && bloodied) advantages.push('Bloodied Fury');
  if (hasFaerieFire(combatState, targetId)) advantages.push('Faerie Fire');
  if (hasBlur(combatState, targetId) && !attackerPerceives) disadvantages.push('Blur');
  if (hasForesight(combatState, targetId)) disadvantages.push('Foresight');
  if (hasHolyAura(combatState, targetId)) disadvantages.push('Holy Aura');
  disadvantages.push(...cursedAttackSources(combatState, attackerId, targetId));
  if (isDancing(combatState, targetId)) advantages.push('Irresistible Dance');
  const vexEffect = getActiveVexEffect(combatState, attackerId, targetId);
  if (vexEffect) advantages.push(VEX_ADVANTAGE_SOURCE);
  const studiedEffect = getStudiedAttacksEffect(combatState, attackerId, targetId);
  if (studiedEffect) advantages.push(STUDIED_ATTACKS_ADVANTAGE_SOURCE);
  const steadyAimEffect = getSteadyAimAdvantage(combatState, attackerId);
  if (steadyAimEffect) advantages.push('Rogue: Steady Aim');
  const sapEffects = getActiveSapEffects(combatState, attackerId);
  if (sapEffects.length) disadvantages.push(SAP_DISADVANTAGE_SOURCE);
  for (const effect of nextAttackDisadvantageEffects(combatState, attackerId)) disadvantages.push(effect.definitionId === 'spell:vicious-mockery' ? 'Vicious Mockery' : 'Ray of Enfeeblement');
  // Ray of Enfeeblement: a monster's melee attack counts as Strength-based unless it names another ability.
  if (enfeebledAttackSource(combatState, attackerId) && (/^str/i.test(String(attack?.ability || '')) || (!attack?.ability && attack?.isMeleeAttack === true))) disadvantages.push('Ray of Enfeeblement');
  if ((combatState?.activeEffects || []).some((effect) =>
    effect?.definitionId === 'reckless-attack' && effect?.targetCombatantId === targetId)) {
    advantages.push('Target used Reckless Attack');
  }
  if (preciseHunterApplies(attacker, combatState?.activeEffects, attackerId, targetId)) advantages.push(PRECISE_HUNTER_ADVANTAGE_SOURCE);
  if (multiattackDefenseApplies(combatState?.activeEffects, attackerId, targetId)) disadvantages.push(MULTIATTACK_DEFENSE_DISADVANTAGE_SOURCE);
  if (guidingBoltEffects(combatState, targetId).some(e=>e.definitionId==='spell:guiding-bolt')) advantages.push('Guiding Bolt');
  if (guidingBoltEffects(combatState, targetId).some(e=>e.definitionId==='monk:stunning-strike:advantage')) advantages.push('Stunning Strike');
  const resolvedAdvantages = withoutSuppressedAdvantages(advantages, suppressedAdvantageSources);
  return {
    mode: resolvedAdvantages.length && !disadvantages.length ? 'advantage' : disadvantages.length && !resolvedAdvantages.length ? 'disadvantage' : 'normal',
    advantageSources: resolvedAdvantages,
    disadvantageSources: disadvantages,
    // Preserve source-level suppressions for the attack producer, which adds
    // attacker-specific sources (such as Reckless Attack) immediately before
    // the dice are rolled.
    suppressedAdvantageSources: [...suppressedAdvantageSources],
    ...(elusive ? { elusive: true } : {}),
    vexEffectId: vexEffect?.id || null,
    studiedEffectId: studiedEffect?.id || null,
    sapEffectIds: sapEffects.map((effect) => effect.id),
    steadyAimEffectId: steadyAimEffect?.id || null,
  };
};

// Rules orchestration is dependency-injected so the established dice roller, HP writer,
// and combat log remain the source of truth. Target validation and applied-damage
// processing are deliberate extension points for range/cover and resistances later.
export async function resolveAttack({
  attackerId,
  // The attacking creature's record when no hit-feature context carries it (monster attacks).
  attackerEntity = null,
  sourceType = null,
  targetId,
  attack,
  target,
  rollAttack,
  rollDamage,
  applyDamage,
  writeLog,
  validateTarget = () => ({ valid: true }),
  combatState,
  calculateAppliedDamage = ({ rawDamage, damageType, target: recipient, source }) => applyDamageTypeTraits({ type: damageType, damage: rawDamage }, recipient, { combatState, targetCombatantId: targetId, source }).finalDamage,
  advantageSources,
  disadvantageSources,
  suppressedAdvantageSources,
  brutalStrike,
  evaluateHitFeatures,
  evaluateMissFeatures,
  resolveFeatureDecision,
  claimFeatureUsage,
  hitFeatureContext,
  onAttackResolved,
  onAttackRollResolved,
  // Peerless Aim (Boon of Combat Prowess): asked on a miss; resolves true when the player turned it into a hit and the
  // once-per-turn use was claimed.
  resolvePeerlessAim,
  // Stroke of Luck (Rogue 20): asked on a miss Peerless Aim didn't fix; resolves true when the d20 became a 20 (a
  // Critical Hit) and the once-per-rest use was claimed.
  resolveStrokeOfLuck,
  // Heroic Inspiration: asked on a miss nothing else fixed; resolves { naturalRoll } with the rerolled d20 once spent.
  resolveHeroicInspiration,
  // Boon of Fate: asked on a miss nothing else fixed (not a natural 1); resolves { bonus, rolls } (2d4) once spent.
  resolveFate,
  // A held Bardic Inspiration die: asked on a miss nothing else fixed (not a natural 1); resolves { bonus, rolls, sides } once spent.
  resolveBardicDie,
  // Peerless Skill (College of Lore 14): the Bard's own die on a miss nothing else fixed; resolves { bonus, rolls, sides } once rolled.
  resolvePeerlessSkill,
  // Cutting Words on this hit (the DM's monster attacks): `kind` is 'attack', then 'damage'; resolves { penalty, rolls, sides, by } or null.
  resolveCuttingWords,
  // Another creature's Improve Fate on this hit (the DM's monster attacks): resolves { penalty, rolls, by } or null.
  resolveFateWindow,
  onAttackRollCommitted,
  onDamageResolved,
  commitVexAttackRoll,
  applyVexOnHit,
  // Studied Attacks (Fighter 13): consume the mark with the roll it advantaged; mark the creature after a miss.
  commitStudiedAttackRoll,
  applyStudiedOnMiss,
  applySlowOnHit,
  commitSapAttackRoll,
  commitSteadyAimAttackRoll,
  applySapOnHit,
  applyPushOnHit,
  resolveTargetedSave,
  resolveCunningStrike,
  resolveDefenderReaction,
  defenderReactionRequired = false,
  spatialContext,
  actionEconomy,
  attackOrigin = 'ordinary',
  attackResolutionId = null,
  parentResolutionId = null,
  rollComponentDie,
  proneProximity,
}) {
  if (!attackerId || !targetId || !attack?.id) throw new Error('Attack participants or attack are missing.');
  if (evaluateHitFeatures != null && typeof evaluateHitFeatures !== 'function') throw new Error('evaluateHitFeatures must be a function.');
  if (evaluateMissFeatures != null && typeof evaluateMissFeatures !== 'function') throw new Error('evaluateMissFeatures must be a function.');
  if (resolveFeatureDecision != null && typeof resolveFeatureDecision !== 'function') throw new Error('resolveFeatureDecision must be a function.');
  if (claimFeatureUsage != null && typeof claimFeatureUsage !== 'function') throw new Error('claimFeatureUsage must be a function.');
  const armorClass = Number(target?.armorClass);
  const hpBefore = Number(target?.currentHp);
  if (!Number.isFinite(armorClass) || !Number.isFinite(hpBefore)) throw new Error('Target AC or HP is unavailable.');
  const validation = validateTarget({ attackerId, targetId, attack, target });
  if (validation?.valid === false) throw new Error(validation.reason || 'That target is not valid.');

  if((combatState?.activeEffects||[]).some(e=>String(e.targetCombatantId)===String(attackerId)&&e.definitionId==='condition:stunned'))throw new Error('A Stunned creature cannot attack.');
  // Charmed (2024): no attack against the charmer, refused before any die is rolled. The HP writer refuses it too.
  // Incapacitated (2024): no actions or Reactions, so no attack roll; the server refuses the attack as well.
  const attackerRecord = hitFeatureContext?.attacker?.entity || attackerEntity || {};
  const incapacitated = incapacitationReason({ ...attackerRecord, tempHealth: attackerRecord.tempHealth ?? attackerRecord.currentHp, combatantId: attackerId }, combatState);
  if (incapacitated) throw new Error(`Can't attack while ${incapacitated}.`);
  if (charmedByEffects(hitFeatureContext?.attacker?.entity || attackerEntity, combatState, attackerId, targetId).length) throw new Error(`A Charmed creature can't attack ${target?.name || 'its charmer'}, who charmed it.`);
  // 2024 cover (creatures in the way, and the map's drawn walls and objects): Half +2 and Three-Quarters +5 to the AC
  // this attack must meet.
  const committedResolutionId = attackResolutionId || createResolutionId(attackerId, targetId, attack.id);
  // A door between them: the map doesn't know whether it's open, so the attacker says. Open, it gives no cover.
  let doorOpen = false;
  if (resolveFeatureDecision && attackThroughDoor({ attackerId, targetId, spatialContext, combatState })) {
    const decision = createDoorDecision(committedResolutionId, target?.name);
    const choice = await resolveFeatureDecision(decision);
    if (!choice || choice.decisionId !== decision.id || !['open', 'closed'].includes(choice.choiceId)) throw new Error('The door question returned an invalid answer.');
    doorOpen = choice.choiceId === 'open';
  }
  const cover = getAttackCover({ attackerId, targetId, spatialContext, combatState, doorOpen });
  // Total Cover: the target can't be targeted directly, so nothing is rolled or spent.
  if (cover?.degree === 'total') throw new Error(`${target?.name || 'That target'} has Total Cover${cover.sources?.length ? ` behind a ${cover.sources[0].toLowerCase()}` : ''} and can't be targeted.`);
  const effectiveArmorClass = armorClass + (cover?.bonus || 0);
  // Cleave, Horde Breaker and curated Magic Actions must be authorized before any d20 exists.
  // Ordinary and Light retain their established post-roll commit.
  // The server names Disadvantage it alone can judge (Escape the Horde reads the target's saved choice).
  let authorizedDisadvantages = [];
  if (actionEconomy?.preRoll === true) {
    if (!['cleave-extra', 'horde-breaker', 'magic', 'opportunity', 'monk'].includes(actionEconomy.kind) || typeof actionEconomy.commit !== 'function') throw new Error('The pre-roll attack authorization is invalid.');
    const commit = await actionEconomy.commit({ resolutionId: committedResolutionId, ...(doorOpen ? { doorOpen } : {}) });
    authorizedDisadvantages = commit?.disadvantageSources || [];
    if (!['spent', 'replayed'].includes(commit?.status)) throw new Error(actionEconomy.kind === 'monk' ? 'This Monk-granted strike is no longer available.' : actionEconomy.kind === 'magic' ? 'No Action is available to cast this spell.' : actionEconomy.kind === 'horde-breaker' ? 'Horde Breaker is no longer available for that target.' : 'Cleave is no longer available for that target.');
  }
  const rollMode = getAttackRollMode({ attackerId, targetId, attack, attacker: hitFeatureContext?.attacker?.entity || attackerEntity, target, combatState, spatialContext, proneProximity, advantageSources, disadvantageSources: [...(disadvantageSources || []), ...authorizedDisadvantages], suppressedAdvantageSources });
  if (brutalStrike) rollMode.brutalStrike = true;
  const mockeryApplies = nextAttackDisadvantageEffects(combatState, attackerId).length > 0;
  const stunningMarkApplies=guidingBoltEffects(combatState,targetId).some(e=>e.definitionId==='monk:stunning-strike:advantage');
  if ((mockeryApplies||stunningMarkApplies) && !combatState?.campaignId) throw new Error('Vicious Mockery requires a campaign identity for this attack.');
  if (guidingBoltEffects(combatState, targetId).length && !mockeryApplies && !stunningMarkApplies) {
    if (!combatState?.campaignId) throw new Error('Guiding Bolt requires a campaign identity for this attack.');
    const claim = await consumeGuidingBolt(combatState.campaignId, { attackerId, targetId, resolutionId: committedResolutionId, turnSequence: combatState.turnSequence });
    if (!claim.effectIds?.length) {
      rollMode.advantageSources = rollMode.advantageSources.filter(source => !['Guiding Bolt','Stunning Strike'].includes(source));
      rollMode.mode = rollMode.advantageSources.length && !rollMode.disadvantageSources.length ? 'advantage' : rollMode.disadvantageSources.length && !rollMode.advantageSources.length ? 'disadvantage' : 'normal';
    }
  }
  if (brutalStrike && rollMode.mode === 'disadvantage') throw new Error('Brutal Strike requires a Strength-based attack that does not have Disadvantage.');
  // Sundering Blow: the next attack roll against the target by anyone but the Barbarian who struck it gets +5. The
  // caller removes the spent effect (result.sunderingBlowEffectId). The roller is told so its shown total includes it;
  // one that reports `situationalBonus` already added it, any other roll gets it added here.
  const sundering = (combatState?.activeEffects || []).find((effect) => effect.definitionId === 'barbarian:sundering-blow'
    && String(effect.targetCombatantId) === String(targetId) && String(effect.sourceCombatantId) !== String(attackerId));
  const rolledAttack = await rollAttack(attack, { ...rollMode, targetCombatantId: targetId,
    ...(sundering ? { situationalBonus: { value: 5, label: 'Sundering Blow' } } : {}) });
  let naturalRoll = Number(rolledAttack?.naturalRoll);
  let attackTotal = Number(rolledAttack?.total) + (sundering ? 5 - (Number(rolledAttack?.situationalBonus) || 0) : 0);
  if (!Number.isFinite(naturalRoll) || !Number.isFinite(attackTotal)) throw new Error('The attack roll could not be completed.');
  // Attack producers resolve attacker-specific sources after target modifiers.
  if (['normal', 'advantage', 'disadvantage'].includes(rolledAttack?.rollMode)) {
    rollMode.mode = rolledAttack.rollMode;
    rollMode.advantageSources = rolledAttack.advantageSources || rollMode.advantageSources;
    rollMode.disadvantageSources = rolledAttack.disadvantageSources || rollMode.disadvantageSources;
  }
  if (actionEconomy && actionEconomy.preRoll !== true) {
    if (!['ordinary', 'light-extra'].includes(actionEconomy.kind) || typeof actionEconomy.commit !== 'function') {
      throw new Error('The attack action-economy authorization is invalid.');
    }
    const commit = await actionEconomy.commit({ resolutionId: committedResolutionId, ...(doorOpen ? { doorOpen } : {}) });
    if (!['spent', 'replayed'].includes(commit?.status)) {
      // A server explanation (out of range, Loading) is shown as given.
      throw new Error(commit?.message ? commit.message : actionEconomy.kind === 'light-extra'
        ? ({
          'candidate-weapon-not-found':'The extra attack weapon could not be matched to your inventory. Refresh your character sheet.',
          'bonus-action-spent':'Your Bonus Action has already been used this turn.',
          'same-weapon-instance':'The Light extra attack requires a different Light weapon.',
          'light-already-spent':'The Light extra attack has already been used this turn.',
          'no-light-trigger':'Make an Attack action attack with a Light weapon first; a hit is not required.',
          'stale-or-inactive-turn':'This attack belongs to an earlier or inactive turn.',
        }[commit?.reason] || 'No Light extra attack is available this turn.')
        : 'No ordinary attack is available this turn.');
    }
  }
  await onAttackRollCommitted?.({ resolutionId: committedResolutionId, targetId, naturalRoll });
  // Consume only after a real, authorized roll; cancellation/interrupted dice
  // leave Mockery intact. One request also settles any Guiding Bolt mark.
  if (mockeryApplies||stunningMarkApplies) {
    const claim = await consumeSpellAttackEffects(combatState.campaignId, { attackerId, targetId,
      resolutionId: committedResolutionId, turnSequence: combatState.turnSequence, naturalD20: naturalRoll });
    if (!['consumed', 'replayed'].includes(claim?.status) || (mockeryApplies&&!claim.disadvantageEffectIds?.length)
      || (guidingBoltEffects(combatState, targetId).length && !claim.effectIds?.length)) {
      throw new Error('Spell attack effects changed before this roll could be committed. Refresh the combat state.');
    }
  }
  // A Vex claim is made only after real dice exist, but before hit/damage can
  // create a replacement token. This ordering makes chaining deterministic.
  if (rollMode.vexEffectId) {
    const claim = await commitVexAttackRoll?.({ effectId: rollMode.vexEffectId, attackerId, targetId, turnSequence: combatState?.turnSequence });
    if (commitVexAttackRoll && !['consumed', 'replayed'].includes(claim?.status)) throw new Error('Vex could not be consumed for this attack roll.');
  }
  if (rollMode.studiedEffectId) {
    const claim = await commitStudiedAttackRoll?.({ effectId: rollMode.studiedEffectId, attackerId, targetId, turnSequence: combatState?.turnSequence });
    if (commitStudiedAttackRoll && !['consumed', 'replayed'].includes(claim?.status)) throw new Error('Studied Attacks could not be consumed for this attack roll.');
  }
  // A committed roll consumes every Sap whose "next attack" condition it
  // satisfies, even when Advantage cancels the final roll mode to normal.
  if (rollMode.sapEffectIds.length) {
    const claim = await commitSapAttackRoll?.({ attackerId, effectIds: rollMode.sapEffectIds, turnSequence: combatState?.turnSequence, resolutionId: committedResolutionId });
    if (commitSapAttackRoll && !['consumed', 'replayed'].includes(claim?.status)) throw new Error('Sap could not be consumed for this attack roll.');
  }
  if (rollMode.steadyAimEffectId) {
    const claim = await commitSteadyAimAttackRoll?.({ effectId: rollMode.steadyAimEffectId, attackerId, turnSequence: combatState?.turnSequence, resolutionId: committedResolutionId });
    if (commitSteadyAimAttackRoll && !['consumed', 'replayed'].includes(claim?.status)) throw new Error('Steady Aim could not be consumed for this attack roll.');
  }
  let critical = isCriticalAttackRoll(hitFeatureContext?.attacker?.entity, attack, naturalRoll);
  let hit = naturalRoll !== 1 && (critical || attackTotal >= effectiveArmorClass);
  // Unconscious target: any hit from within 5 feet is a Critical Hit.
  const unconsciousCritical = () => {
    if (!hasActiveEffect(combatState, targetId, 'condition:unconscious')) return false;
    const feet = attackDistanceFeet({ attackerId, targetId, attacker: hitFeatureContext?.attacker?.entity || attackerEntity, target, spatialContext, proximity: proneProximity });
    // Without map positions, a melee attack stands in for "within 5 feet".
    return feet != null ? feet <= 5 : attack?.isMeleeAttack === true;
  };
  let peerlessAim = false;
  if (!hit && typeof resolvePeerlessAim === 'function') {
    peerlessAim = (await resolvePeerlessAim({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass })) === true;
    hit = peerlessAim;
  }
  let strokeOfLuck = false;
  if (!hit && typeof resolveStrokeOfLuck === 'function') {
    strokeOfLuck = (await resolveStrokeOfLuck({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass })) === true;
    if (strokeOfLuck) { hit = true; critical = true; }
  }
  // Heroic Inspiration: reroll the missed d20 and use the new roll (with the same bonuses).
  let heroicInspiration = null;
  if (!hit && typeof resolveHeroicInspiration === 'function') {
    const reroll = await resolveHeroicInspiration({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass,
      rollMode: rollMode.mode, dice: Array.isArray(rolledAttack?.rolledD20s) ? rolledAttack.rolledD20s : null });
    if (Number.isInteger(reroll?.naturalRoll) && reroll.naturalRoll >= 1 && reroll.naturalRoll <= 20) {
      heroicInspiration = { from: naturalRoll, to: reroll.naturalRoll };
      attackTotal += reroll.naturalRoll - naturalRoll; naturalRoll = reroll.naturalRoll;
      critical = isCriticalAttackRoll(hitFeatureContext?.attacker?.entity, attack, naturalRoll);
      hit = naturalRoll !== 1 && (critical || attackTotal >= effectiveArmorClass);
    }
  }
  // Boon of Fate · Improve Fate: 2d4 added to the missed attack roll (the d20, and so a Critical Hit, is unchanged).
  let fate = null;
  if (!hit && naturalRoll !== 1 && typeof resolveFate === 'function') {
    const added = await resolveFate({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass });
    if (Number.isInteger(added?.bonus) && added.bonus > 0) {
      fate = { bonus: added.bonus, rolls: added.rolls };
      attackTotal += added.bonus;
      hit = attackTotal >= effectiveArmorClass;
    }
  }
  // Bardic Inspiration: the die added to the missed attack roll (the d20, and so a Critical Hit, is unchanged).
  let bardicDie = null;
  if (!hit && naturalRoll !== 1 && typeof resolveBardicDie === 'function') {
    const added = await resolveBardicDie({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass });
    if (Number.isInteger(added?.bonus) && added.bonus > 0) {
      bardicDie = added;
      attackTotal += added.bonus;
      hit = attackTotal >= effectiveArmorClass;
    }
  }
  // Peerless Skill: the hook rolls the die and spends the use only if it turns the miss into a hit.
  let peerlessSkill = null;
  if (!hit && naturalRoll !== 1 && typeof resolvePeerlessSkill === 'function') {
    const added = await resolvePeerlessSkill({ resolutionId: committedResolutionId, targetId, naturalRoll, total: attackTotal, armorClass: effectiveArmorClass });
    if (Number.isInteger(added?.bonus) && added.bonus > 0) {
      peerlessSkill = added;
      attackTotal += added.bonus;
      hit = attackTotal >= effectiveArmorClass;
    }
  }
  // Improve Fate against the attacker (Boon of Fate, 2024): on a hit that is not a Critical Hit, a holder within 60 feet
  // may subtract 2d4 from the roll before damage.
  let fateAgainst = null;
  if (hit && !critical && typeof resolveFateWindow === 'function') {
    const fateWindow = await resolveFateWindow({ resolutionId: committedResolutionId, attackerId, targetId, naturalRoll, attackTotal, armorClass: effectiveArmorClass });
    if (Number.isInteger(fateWindow?.penalty) && fateWindow.penalty > 0) {
      fateAgainst = { penalty: fateWindow.penalty, rolls: fateWindow.rolls, by: fateWindow.by };
      attackTotal -= fateWindow.penalty;
      hit = attackTotal >= effectiveArmorClass;
    }
  }
  // Cutting Words against the attacker: on a hit that isn't a Critical Hit, a College of Lore Bard may subtract their die now.
  let cuttingWords = null;
  if (hit && !critical && typeof resolveCuttingWords === 'function') {
    const cut = await resolveCuttingWords({ kind: 'attack', resolutionId: committedResolutionId, attackerId, targetId, naturalRoll, attackTotal, armorClass: effectiveArmorClass });
    if (Number.isInteger(cut?.penalty) && cut.penalty > 0) {
      cuttingWords = { kind: 'attack', penalty: cut.penalty, sides: cut.sides, by: cut.by };
      attackTotal -= cut.penalty;
      hit = attackTotal >= effectiveArmorClass;
    }
  }
  // A critical the d20 alone doesn't explain (Unconscious target); Stroke of Luck instead makes the d20 itself a 20.
  let automaticCritical = false;
  if (hit && !critical && unconsciousCritical()) { critical = true; automaticCritical = true; }
  await onAttackRollResolved?.({ resolutionId: committedResolutionId, targetId, naturalRoll, hit });
  let resolutionId;
  let rawDamage = 0;
  let damageApplied = 0;
  let brutalStrikeDamage = 0;
  let legacyDamage = 0;
  let damageBeforeTraits = 0;
  // Each damage type's share of damageBeforeTraits, [{ type, before }], so the HP writer applies traits per type.
  let damageByType = null;
  let damageComponents = [];
  // Log detail only: the weapon or spell roll's own parts and which damage trait applied.
  let damageParts = null;
  let damageTraits = null;
  let empoweredStrikes = null;
  let featureResolution;
  const deflected = hit ? getDeflectingWall({ attack, attackerId, targetId, spatialContext, combatState }) : null;
  if (deflected) { hit = false; critical = false; }
  let defenderReaction, shieldedArmorClass, sizeDie;
  if (hit) {
    if (defenderReactionRequired && typeof resolveDefenderReaction !== 'function') {
      throw new Error('A defender reaction handshake is required before damage can be rolled.');
    }
    if (evaluateHitFeatures) {
      resolutionId = committedResolutionId;
      const context = buildResolvedHitContext({
        resolutionId,
        attacker: { ...hitFeatureContext?.attacker, combatantId: attackerId },
        target: { ...hitFeatureContext?.target, combatantId: targetId, armorClass },
        attack: { ...hitFeatureContext?.attack, id: attack.id },
        roll: {
          mode: rollMode.mode,
          advantageSources: rollMode.advantageSources,
          disadvantageSources: rollMode.disadvantageSources,
          naturalRoll: strokeOfLuck ? 20 : naturalRoll,
          total: attackTotal,
          hit: true,
          critical,
          ...(automaticCritical ? { automaticCritical: true } : {}),
        },
        combat: hitFeatureContext?.combat,
      });
      const evaluations = await evaluateHitFeatures(context);
      validateHitFeatureEvaluations(evaluations);
      const confirmedFeatures = [];
      const automaticallyConfirmedFeatures = [];
      const manuallyConfirmedFeatures = [];
      const skippedFeatures = [];
      const claimFailures = [];
      const featureClaims = [];
      const decisions = [];
      for (const evaluation of evaluations) {
        if (evaluation.status === 'automatic' && !evaluation.decision) {
          automaticallyConfirmedFeatures.push(evaluation);
          confirmedFeatures.push(evaluation);
        } else if (evaluation.status === 'confirmation-required' || (evaluation.status === 'automatic' && evaluation.decision)) {
          if (evaluation.decision.resolutionId !== resolutionId || evaluation.decision.id !== `${resolutionId}:${evaluation.featureId}`) {
            throw new Error(`Hit feature "${evaluation.featureId}" decision identity is invalid.`);
          }
          if (!resolveFeatureDecision) throw new Error(`Hit feature "${evaluation.featureId}" requires a decision resolver.`);
          const decisionResult = await resolveFeatureDecision(evaluation.decision);
          const supportedChoiceIds = evaluation.decision.choices.filter((choice) => !choice.disabled).map((choice) => choice.id);
          // A decision with a selection (Improved Cunning Strike) also answers `<prefix><id>+<id>`; resolveDecision checks the ids.
          const selection = evaluation.decision.selection;
          const selected = Boolean(selection?.prefix) && typeof decisionResult?.choiceId === 'string' && decisionResult.choiceId.startsWith(selection.prefix)
            && decisionResult.choiceId.length > selection.prefix.length;
          if (!decisionResult || typeof decisionResult !== 'object' || decisionResult.decisionId !== evaluation.decision.id ||
              !(supportedChoiceIds.includes(decisionResult.choiceId) || selected)) {
            throw new Error(`Hit feature "${evaluation.featureId}" decision resolver returned an invalid result.`);
          }
          decisions.push({ decisionId: decisionResult.decisionId, featureId: evaluation.featureId, choiceId: decisionResult.choiceId });
          const chosen = evaluation.decision.choices.find((choice) => choice.id === decisionResult.choiceId);
          if (selected || (chosen?.action || decisionResult.choiceId) === 'apply') {
            const resolvedEvaluation = typeof evaluation.resolveDecision === 'function' ? evaluation.resolveDecision(decisionResult.choiceId) : evaluation;
            manuallyConfirmedFeatures.push(resolvedEvaluation);
            confirmedFeatures.push(resolvedEvaluation);
          } else {
            skippedFeatures.push(evaluation);
          }
        }
      }
      for (const evaluation of [...confirmedFeatures]) {
        if (!evaluation.claims.length) continue;
        let accepted = true;
        for (const claim of evaluation.claims) {
          let claimResult;
          try {
            claimResult = claimFeatureUsage ? await claimFeatureUsage(claim) : { status: 'rejected', reason: 'claim-service-unavailable' };
          } catch (error) {
            claimResult = { status: 'rejected', reason: error?.message || 'claim-service-error' };
          }
          featureClaims.push({ featureId: evaluation.featureId, request: claim, result: claimResult });
          if (!['claimed', 'replayed'].includes(claimResult?.status)) {
            accepted = false;
            claimFailures.push({ featureId: evaluation.featureId, featureLabel: evaluation.featureLabel || evaluation.featureId, ...claimResult });
            break;
          }
        }
        if (!accepted) confirmedFeatures.splice(confirmedFeatures.indexOf(evaluation), 1);
      }
      featureResolution = { evaluations, confirmedFeatures, automaticallyConfirmedFeatures, manuallyConfirmedFeatures, skippedFeatures, decisions, featureClaims, claimFailures };
    }
    const approvedComponents = featureResolution?.confirmedFeatures.flatMap(({ components }) => components) || [];
    // Confirmed features marked modifiesRoll change how the roller rolls (Savage Attacker rerolls the weapon dice).
    const rollFeatureIds = featureResolution?.confirmedFeatures.filter(({ modifiesRoll }) => modifiesRoll === true).map(({ featureId }) => featureId) || [];
    const rolledDamage = await rollDamage(attack, {
      critical,
      brutalStrike,
      ...(approvedComponents.length > 0 ? { damageComponents: approvedComponents, inheritedDamageType: attack.damageType || null } : {}),
      ...(rollFeatureIds.length ? { rollFeatureIds } : {}),
    });
    legacyDamage = Number(typeof rolledDamage === 'object' ? rolledDamage?.total : rolledDamage);
    brutalStrikeDamage = Number(rolledDamage?.brutalStrikeDamage) || 0;
    if (Array.isArray(rolledDamage?.parts)) damageParts = rolledDamage.parts;
    if (!Number.isFinite(legacyDamage)) throw new Error('The damage roll could not be completed.');
    // The UI roller can return component records produced from the same 3D dice
    // that were displayed. Non-visual callers retain the pure roller fallback.
    damageComponents = Array.isArray(rolledDamage?.damageComponents)
      ? rolledDamage.damageComponents
      : rollDamageComponents(approvedComponents, {
        critical,
        inheritedDamageType: attack.damageType || null,
        ...(rollComponentDie ? { rollDie: rollComponentDie } : {}),
      });
    rawDamage = legacyDamage + damageComponents.reduce((sum, component) => sum + component.total, 0);
    // Enlarge/Reduce: a d4 more or less on a weapon or Unarmed hit, and never below 1.
    const sizeMode = !attack.spell && attack.kind !== 'spell' ? sizeChangeMode(combatState, attackerId) : null;
    if (sizeMode) {
      sizeDie = { mode: sizeMode, value: 1 + Math.floor(Math.random() * 4) };
      rawDamage = sizeMode === 'enlarge' ? rawDamage + sizeDie.value : Math.max(1, rawDamage - sizeDie.value);
    }
    // Cutting Words on the damage roll, for a Bard who waited for it or a Critical Hit that skipped the attack prompt.
    if (typeof resolveCuttingWords === 'function') {
      const cut = await resolveCuttingWords({ kind: 'damage', resolutionId: committedResolutionId, attackerId, targetId, damage: rawDamage });
      if (Number.isInteger(cut?.penalty) && cut.penalty > 0) {
        cuttingWords = { kind: 'damage', penalty: cut.penalty, sides: cut.sides, by: cut.by };
        rawDamage = Math.max(0, rawDamage - cut.penalty);
      }
    }
    // Defender decisions use the actual rolled total, before mitigation or HP writes.
    defenderReaction = await resolveDefenderReaction?.({
      trigger:'attack-hit',parentAttackResolutionId:committedResolutionId,
      sourceCombatantId:attackerId,targetCombatantId:targetId,attackId:attack.id,attackName:attack.name||'',attackRoll:true,hit:true,
      rawDamage,damageTypes:[attack.damageType,...(attack.damageTypes||[]),...damageComponents.map(c=>c.type||c.damageType)].filter(Boolean),
      attackMode:attack.isMeleeAttack===true?'melee':'ranged',attackTotal,armorClass:effectiveArmorClass,critical,
    });
    const modified = applyIncomingDamageModifiers(rawDamage, defenderReaction?.incomingDamageModifiers || []);
    damageBeforeTraits = modified.finalDamageApplied;
    const damageSource = hitFeatureContext?.attacker?.entity || attackerEntity || null;
    // 2024: resistance, vulnerability and immunity apply to each damage type separately.
    damageByType = splitDamageByType([
      ...(damageParts || [{ type: attack.damageType, value: legacyDamage }]),
      ...damageComponents.map((component) => ({ type: component.type || component.damageType, value: component.total })),
    ], modified.finalDamageApplied, attack.damageType);
    // Empowered Strikes (Monk 6): the Unarmed Strike's own damage becomes Force when the target loses more HP to it.
    if (attack.empoweredStrikes) {
      const baseType = String(attack.damageType || 'bludgeoning').toLowerCase();
      damageByType = damageByType.map((entry) => {
        if (String(entry.type || baseType).toLowerCase() !== baseType) return entry;
        const type = empoweredStrikesType(baseType, (candidate) => Number(calculateAppliedDamage({ rawDamage: entry.before, damageType: candidate, target, source: damageSource })) || 0);
        if (type === baseType) return entry;
        empoweredStrikes = 'force';
        return { ...entry, type };
      });
    }
    damageApplied = damageByType.reduce((sum, { type, before }) => sum + Math.max(0, Number(calculateAppliedDamage({ rawDamage: before, damageType: type, target, source: damageSource })) || 0), 0);
    for (const { type, before } of damageByType) {
      const traits = applyDamageTypeTraits({ type, damage: before }, target || {}, { combatState, targetCombatantId: targetId, source: damageSource });
      if (!damageTraits && (traits.immunity || traits.resistance || traits.vulnerability)) damageTraits = { immunity: traits.immunity, resistance: traits.resistance, vulnerability: traits.vulnerability, type: traits.type };
    }
    if (defenderReaction) defenderReaction = { ...defenderReaction, ...modified };
    // Shield's +5 AC counts against this attack: it misses, so nothing that needs a hit follows.
    if (defenderReaction?.opportunity?.attackNegated) { hit = false; critical = false; rawDamage = 0; shieldedArmorClass = effectiveArmorClass + SHIELD_AC_BONUS; }
    // Mirror Image: a duplicate took the hit, so this wasn't a miss either.
    if (defenderReaction?.opportunity?.mirrorImage) { shieldedArmorClass = undefined; damageApplied = 0; damageBeforeTraits = 0; damageByType = null; damageTraits = null; }
  }
  let missFeatureResolution;
  if (!hit && evaluateMissFeatures && !defenderReaction?.opportunity?.mirrorImage) {
    const resolvedMissAttack = {
      ...attack,
      abilityModifier: hitFeatureContext?.attack?.abilityModifier ?? attack.abilityModifier,
    };
    const evaluations = await evaluateMissFeatures({
      attack: resolvedMissAttack,
      outcome: 'miss',
      resolutionId: committedResolutionId,
      attackerId,
      targetId,
      target,
    });
    if (!Array.isArray(evaluations)) throw new Error('evaluateMissFeatures must return an array.');
    const decisions = [];
    const appliedFeatures = [];
    const skippedFeatures = [];
    for (const evaluation of evaluations) {
      const decision = evaluation?.decision;
      // An automatic miss feature (Potent Cantrip) asks nothing.
      if (evaluation?.status === 'automatic' && evaluation.featureId && Number(evaluation.fixedDamage) > 0) { appliedFeatures.push(evaluation); continue; }
      if (!evaluation?.featureId || evaluation.status !== 'confirmation-required' ||
          !Number.isFinite(Number(evaluation.fixedDamage)) || Number(evaluation.fixedDamage) <= 0 ||
          !decision || decision.featureId !== evaluation.featureId || decision.resolutionId !== committedResolutionId ||
          decision.id !== `${committedResolutionId}:${evaluation.featureId}`) {
        throw new Error('Miss feature evaluation is invalid.');
      }
      if (!resolveFeatureDecision) throw new Error(`Miss feature "${evaluation.featureId}" requires a decision resolver.`);
      const choice = await resolveFeatureDecision(decision);
      if (!choice || choice.decisionId !== decision.id || !['apply', 'skip'].includes(choice.choiceId)) {
        throw new Error(`Miss feature "${evaluation.featureId}" decision resolver returned an invalid result.`);
      }
      decisions.push({ decisionId: choice.decisionId, featureId: evaluation.featureId, choiceId: choice.choiceId });
      if (choice.choiceId === 'apply') appliedFeatures.push(evaluation);
      else skippedFeatures.push(evaluation);
    }
    // Miss effects currently resolve one fixed-damage source. Keeping that
    // source generic makes the seam reusable without inventing mixed-type
    // damage aggregation before a second miss feature actually needs it.
    const appliedMissFeature = appliedFeatures[0];
    if (appliedMissFeature) {
      rawDamage = Number(appliedMissFeature.fixedDamage);
      damageBeforeTraits = rawDamage;
      damageApplied = Math.max(0, Number(calculateAppliedDamage({
        rawDamage,
        damageType: appliedMissFeature.damageType,
        target,
      })) || 0);
    }
    if (evaluations.length) missFeatureResolution = { evaluations, appliedFeatures, skippedFeatures, decisions };
  }
  // Acid Arrow splashes half its initial damage on a miss. A Shield that turned a hit into a miss stops it.
  if (!hit && attack.missHalfDamage && !shieldedArmorClass && !missFeatureResolution?.appliedFeatures?.length) {
    const rolled = await rollDamage(attack, { critical: false });
    const half = Math.floor(Number(typeof rolled === 'object' ? rolled?.total : rolled) / 2);
    if (half > 0) {
      const splash = { featureId: 'spell:miss-half', status: 'automatic', label: `${attack.name || 'Spell'} splash`, fixedDamage: half, damageType: attack.damageType };
      rawDamage = half; damageBeforeTraits = half;
      damageApplied = Math.max(0, Number(calculateAppliedDamage({ rawDamage: half, damageType: attack.damageType, target })) || 0);
      missFeatureResolution = { evaluations: [splash], appliedFeatures: [splash], skippedFeatures: [], decisions: [] };
    }
  }
  let hpAfter = Math.max(0, hpBefore - damageApplied);
  const result = {
    type: 'attack-resolution', attackerId, targetId, attackId: attack.id, attackName: attack.name || '',
    naturalRoll, attackTotal, targetArmorClass: shieldedArmorClass ?? effectiveArmorClass,
    ...(cover ? { cover: { degree: cover.degree, bonus: cover.bonus, label: cover.label, ...(cover.blockerIds ? { blockerIds: cover.blockerIds } : {}), ...(cover.sources ? { sources: cover.sources } : {}) } } : {}),
    outcome: critical && hit ? 'critical-hit' : hit ? 'hit' : 'miss',
    damage: rawDamage, damageBeforeTraits, damageApplied, damageType: empoweredStrikes || attack.damageType || '', brutalStrikeDamage,
    ...(damageByType?.length > 1 ? { damageByType } : {}),
    ...(empoweredStrikes ? { empoweredStrikes } : {}),
    hpBefore, hpAfter, rollMode: rollMode.mode, advantageSources: rollMode.advantageSources,
    disadvantageSources: rollMode.disadvantageSources, timestamp: Date.now(),
    resolutionId: resolutionId || committedResolutionId,
    ...(parentResolutionId ? { parentResolutionId } : {}),
    ...(attackOrigin !== 'ordinary' ? { origin: attackOrigin } : {}),
    ...(peerlessAim ? { peerlessAim: true } : {}),
    ...(strokeOfLuck ? { strokeOfLuck: true } : {}),
    ...(heroicInspiration ? { heroicInspiration } : {}),
    ...(fate ? { fate } : {}),
    ...(bardicDie ? { bardicDie } : {}),
    ...(peerlessSkill ? { peerlessSkill } : {}),
    ...(cuttingWords ? { cuttingWords } : {}),
    ...(fateAgainst ? { fateAgainst } : {}),
    ...(sundering ? { sunderingBlowEffectId: sundering.id } : {}),
    ...(sizeDie ? { sizeDie } : {}),
    ...(doorOpen ? { doorOpen } : {}),
    ...(deflected ? { deflectedBy: deflected.name } : {}),
  };
  if (featureResolution) result.featureResolution = featureResolution;
  if (damageParts) result.damageParts = damageParts;
  // The damage after resistance etc., kept apart from damageApplied, which becomes the HP actually lost.
  if (damageTraits) result.damageTraits = { ...damageTraits, afterTraits: damageApplied };
  if (hit && !result.damageComponents && damageComponents.length) result.damageComponents = damageComponents;
  if (defenderReaction) {
    result.defenderReaction = defenderReaction;
    result.rolledAttackDamage = defenderReaction.rolledAttackDamage;
    result.incomingDamageModifiers = defenderReaction.incomingDamageModifiers;
    result.finalDamageApplied = damageApplied;
  }
  if (missFeatureResolution) result.missFeatureResolution = missFeatureResolution;
  const appliedGraze = missFeatureResolution?.appliedFeatures.find(({ featureId }) => featureId === GRAZE_DEFINITION_ID);
  if (appliedGraze) {
    result.grazeApplied = true;
    result.grazeDamage = Number(appliedGraze.fixedDamage);
  }
  if (featureResolution || defenderReaction) {
    result.damageComponents = damageComponents;
    result.damageBreakdown = {
      legacyTotal: legacyDamage,
      featureTotal: damageComponents.reduce((sum, component) => sum + component.total, 0),
      rolledTotal: rawDamage,
      appliedTotal: damageApplied,
      components: damageComponents,
      incomingDamageModifiers: defenderReaction?.incomingDamageModifiers || [],
      finalDamageApplied: damageApplied,
    };
  }
  const missDamage = !appliedGraze && missFeatureResolution?.appliedFeatures[0];
  if (missDamage) result.missDamage = { label: missDamage.label || 'Miss damage', amount: Number(missDamage.fixedDamage) };
  if (hit || result.grazeApplied || result.missDamage) {
    // The HP writer owns the authoritative before/after values.  In particular,
    // callers must not log an optimistic token-only prediction as persisted HP.
    const applied = await applyDamage({ targetId, hpBefore, hpAfter, damageApplied, result });
    if (applied) {
      if (applied.resistanceReduction) result.resistanceReduction = applied.resistanceReduction;
      if (Number.isFinite(Number(applied.previousHp))) result.hpBefore = Number(applied.previousHp);
      if (Number.isFinite(Number(applied.currentHp))) result.hpAfter = Number(applied.currentHp);
      if (Number.isFinite(Number(applied.appliedDamage))) result.damageApplied = Number(applied.appliedDamage);
      hpAfter = result.hpAfter;
      if (result.damageBreakdown) { result.damageBreakdown.appliedTotal = result.damageApplied; result.damageBreakdown.finalDamageApplied = result.damageApplied; }
      if (result.defenderReaction) result.finalDamageApplied = result.damageApplied;
    }
  }
  if (result.damageApplied > 0) {
    const actualHpLost = Math.max(0, result.hpBefore - result.hpAfter);
    await onDamageResolved?.({
      damageEventId: result.resolutionId,
      sourceCombatantId: attackerId,
      targetCombatantId: targetId,
      sourceType,
      attackId: result.attackId,
      attackName: result.attackName,
      damageType: result.damageType,
      actualHpLost,
      damageTaken: actualHpLost,
      sourcePosition: result.sourcePosition,
      targetPosition: result.targetPosition,
      result,
    });
  }
  // Cunning Strike belongs to the claimed Sneak Attack and resolves after the
  // authoritative HP write, before independent Weapon Mastery processing.
  const sneakAttack = featureResolution?.confirmedFeatures.find((feature) => feature.featureId === 'rogue:sneak-attack');
  if (sneakAttack?.sneakAttack) {
    result.sneakAttack = {
      ...sneakAttack.sneakAttack,
      criticalRolledDice: sneakAttack.sneakAttack.remainingDice * (critical ? 2 : 1),
    };
    result.cunningStrike = { selectedEffects: sneakAttack.cunningStrike?.selectedEffects || [] };
    if (result.cunningStrike.selectedEffects.length) {
      if (!resolveCunningStrike) throw new Error('Cunning Strike requires its post-damage resolver.');
      result.cunningStrike.resolutions = [];
      for (const effect of result.cunningStrike.selectedEffects) {
        result.cunningStrike.resolutions.push(await resolveCunningStrike({
          effect, parentResolutionId: result.resolutionId, attackerId, targetId, target,
          attacker: hitFeatureContext?.attacker?.entity, combatState, hpBefore: result.hpBefore, hpAfter: result.hpAfter,
        }));
      }
    }
  }
  if (qualifiesForSlow({ attack, outcome: result.outcome, damageApplied: result.damageApplied, attackerId, targetId, combatState })) {
    if (!resolveFeatureDecision) throw new Error('Slow requires a decision resolver.');
    const decision = createSlowDecision(result.resolutionId);
    const choice = await resolveFeatureDecision(decision);
    if (!choice || choice.decisionId !== decision.id || !['apply', 'skip'].includes(choice.choiceId)) {
      throw new Error('Slow decision resolver returned an invalid result.');
    }
    result.slowDecision = choice.choiceId;
    if (choice.choiceId === 'apply') {
      const applied = await applySlowOnHit?.({
        attackerId, targetId, turnSequence: combatState.turnSequence, resolutionId: result.resolutionId,
        sourceWeaponInstanceId: attack.weaponInstanceId,
      });
      if (applySlowOnHit && !['applied', 'replayed'].includes(applied?.status)) throw new Error('Slow could not be applied.');
      result.slowApplied = Boolean(applySlowOnHit);
    }
  }
  if (qualifiesForSap({ attack, outcome: result.outcome, attackerId, targetId, combatState })) {
    const applied = await applySapOnHit?.({
      attackerId, targetId, turnSequence: combatState.turnSequence, resolutionId: result.resolutionId,
      sourceWeaponInstanceId: attack.weaponInstanceId,
    });
    if (applySapOnHit && !['applied', 'replayed'].includes(applied?.status)) throw new Error('Sap could not be applied.');
    result.sapApplied = Boolean(applySapOnHit);
  }
  const resolvedAttackMath = {
    ...attack,
    ability: hitFeatureContext?.attack?.ability ?? attack.ability ?? null,
    abilityModifier: hitFeatureContext?.attack?.abilityModifier ?? attack.abilityModifier,
    proficiencyBonus: hitFeatureContext?.attack?.proficiencyBonus ?? attack.proficiencyBonus,
  };
  if (qualifiesForTopple({ attack, outcome: result.outcome, targetId, combatState })) {
    if (!resolveFeatureDecision) throw new Error('Topple requires a decision resolver.');
    const dc = getToppleSaveDC(resolvedAttackMath);
    const decision = createToppleDecision(result.resolutionId, dc);
    const choice = await resolveFeatureDecision(decision);
    if (!choice || choice.decisionId !== decision.id || !['apply', 'skip'].includes(choice.choiceId)) throw new Error('Topple decision resolver returned an invalid result.');
    result.toppleDecision = choice.choiceId;
    if (choice.choiceId === 'apply') {
      if (!resolveTargetedSave) throw new Error('Topple requires the targeted save service.');
      const request = createToppleSaveRequest({ resolutionId: result.resolutionId, attackerId, targetId, attack: resolvedAttackMath, target });
      const save = await resolveTargetedSave(request);
      if (save?.status !== 'resolved' || save.requestId !== request.requestId) throw new Error('Targeted save service returned an invalid result.');
      result.targetedSave = save;
      result.toppleApplied = save.success === false;
    }
  }
  const pushInput = {
    attack, outcome: result.outcome, attackerId, targetId,
    attacker: hitFeatureContext?.attacker?.entity, target, combatState, spatialContext,
  };
  if (qualifiesForPush(pushInput)) {
    if (!resolveFeatureDecision) throw new Error('Push requires a decision resolver.');
    const options = getPushOptions(pushInput);
    const decision = createPushDecision(result.resolutionId, options);
    const choice = await resolveFeatureDecision(decision);
    const selected = options.find(({ distanceFeet }) => choice?.choiceId === `push-${distanceFeet}`);
    if (!choice || choice.decisionId !== decision.id || (choice.choiceId !== 'skip' && !selected)) {
      throw new Error('Push decision resolver returned an invalid result.');
    }
    result.pushDecision = choice.choiceId;
    if (selected) {
      const pushSpatial = getPushSpatialContext(pushInput);
      const applied = await applyPushOnHit?.({
        attackerId, targetId, turnSequence: combatState.turnSequence, resolutionId: result.resolutionId,
        sourceWeaponInstanceId: attack.weaponInstanceId,
        baseWeaponKey: attack.weapon?.index || attack.weapon?.name,
        distanceFeet: selected.distanceFeet,
        sourcePosition: { x: pushSpatial.sourceToken.x, y: pushSpatial.sourceToken.y },
        targetPosition: { x: pushSpatial.targetToken.x, y: pushSpatial.targetToken.y },
      });
      if (applyPushOnHit && !['applied', 'replayed'].includes(applied?.status)) {
        throw new Error(applied?.message || 'Push could not be applied.');
      }
      result.pushApplied = Boolean(applyPushOnHit);
      if (applied?.movement) result.pushMovement = applied.movement;
    }
  }
  await writeLog(result);
  await onAttackResolved?.({ ...result, brutalStrike: Boolean(brutalStrike) });
  // Apply only after other resolution hooks have persisted their changes. The
  // server performs an atomic filter-and-append against the latest effect list.
  if (qualifiesForVex({ attack, outcome: result.outcome, damageApplied: result.damageApplied })) {
    await applyVexOnHit?.({ attackerId, targetId, turnSequence: combatState?.turnSequence, resolutionId: result.resolutionId });
  }
  if (result.outcome === 'miss' && hasStudiedAttacks(hitFeatureContext?.attacker?.entity || attackerEntity)) {
    await applyStudiedOnMiss?.({ attackerId, targetId, turnSequence: combatState?.turnSequence, resolutionId: result.resolutionId });
  }
  return result;
}


